import { observer } from 'mobx-react-lite'
import { type CloseJobAsAdminDialogViewModel } from './CloseJobAsAdminDialogViewModel'
import { useFeatures } from '@/contexts/root-store-context'
import { CustomSelect } from '@/components/inputs/CustomSelect'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { CheckboxInputContainer } from '@/components/inputs/CheckboxInputContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { CloseJobAsAdminDialog } from '@/features/jobs/details/components/CloseJobAsAdminDialog'

export interface CloseJobAsAdminDialogContainerProps {
  vm: CloseJobAsAdminDialogViewModel
}

export const CloseJobAsAdminDialogContainer = observer(
  function CloseJobAsAdminDialogContainer({
    vm,
  }: CloseJobAsAdminDialogContainerProps) {
    const { shellInfo } = useFeatures(({ shell }) => ({
      shellInfo: shell.shellInfo,
    }))

    const billingMethodSelectProps = useCustomSelectStateFromItemSource({
      itemSource: vm.fetchBillingMethods,
    })

    return (
      <CloseJobAsAdminDialog
        showing={vm.modal.showing}
        corporateMode={shellInfo.corporateMode}
        jobName={vm.jobName}
        resolutionField={
          <SelectContainer
            label="Resolution"
            model={vm}
            field="resolution"
            items={vm.resolutionSelectItems}
            help="The resolution for this job."
            isDisabled={vm.isScopeChangeRequestPending}
          />
        }
        reasonField={
          <TextInputContainer
            label="Reason"
            model={vm}
            field="reason"
            help="Why are you closing this job? This text is public and can be seen by the Client and the Pros."
            isDisabled={vm.isScopeChangeRequestPending}
          />
        }
        shouldBillJobField={
          <CheckboxInputContainer
            model={vm}
            field="shouldBillJob"
            label={vm.billingOperationMessage}
            isDisabled={vm.isScopeChangeRequestPending}
          />
        }
        billingMethodField={
          <CustomSelect
            searchResultsPlacement="above"
            multi={false}
            {...billingMethodSelectProps}
            value={vm.selectedBillingMethod}
            onChange={vm.setBillingMethod}
            itemToString={(i) => i.displayName}
            isDisabled={vm.isScopeChangeRequestPending}
            testId="custom-select"
          />
        }
        billingAllowed={vm.billingAllowed}
        shouldCollectBillingMethod={vm.shouldCollectBillingMethod}
        isScopeChangeRequestPending={vm.isScopeChangeRequestPending}
        isCloseJobPending={vm.closeJob.pending}
        isCloseJobRejected={vm.closeJob.rejected}
        closeJobError={vm.closeJob.error}
        onClose={vm.modal.close}
        onCloseJob={vm.closeJob}
        onDismissError={vm.closeJob.reset}
        submitButtonTestId="close-job-as-admin-submit-button"
      />
    )
  },
)
